/**
 * Extracts and validates form data from LLM JSON responses
 * Handles type conversions and schema validation
 */

import type { BaseEntity, SimpleTableConfig } from "../../UniversalCRMTable/types/index";
import type { ExtractedFormData } from "../types/ai-assistant";

/** A parsed LLM JSON object → field name → raw heterogeneous value. */
type ParsedLLMData = Record<string, unknown>;

/**
 * Attempts to parse JSON from LLM response
 * Handles cases where LLM wraps JSON in markdown code blocks
 */
function parseJSONResponse(response: string): ParsedLLMData | null {
  if (!response || typeof response !== "string") {
    return null;
  }

  const trimmed = response.trim();

  // Try direct JSON parse first
  try {
    return JSON.parse(trimmed);
  } catch {
    // Try removing markdown code blocks
    const jsonMatch = trimmed.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
    if (jsonMatch) {
      try {
        return JSON.parse(jsonMatch[1]);
      } catch {
        // Continue to next attempt
      }
    }

    // Try finding JSON object in response (greedy match)
    const objectMatch = trimmed.match(/\{[\s\S]*\}/);
    if (objectMatch) {
      try {
        return JSON.parse(objectMatch[0]);
      } catch {
        // Continue to next attempt
      }
    }

    // Try finding JSON array in response
    const arrayMatch = trimmed.match(/\[[\s\S]*\]/);
    if (arrayMatch) {
      try {
        return JSON.parse(arrayMatch[0]);
      } catch {
        // Continue to next attempt
      }
    }

    return null;
  }
}

/**
 * Converts a value to the target field type
 */
function convertToFieldType(
  value: unknown,
  fieldType: string
): number | boolean | string | string[] | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }

  switch (fieldType) {
    case "number": {
      const num = Number(value);
      return isNaN(num) ? undefined : num;
    }

    case "boolean":
      if (typeof value === "boolean") return value;
      if (typeof value === "string") {
        return value.toLowerCase() === "true" || value === "1";
      }
      return Boolean(value);

    case "date":
      // Handle various date formats
      if (typeof value === "string") {
        // Try ISO format first
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return value; // Return as string in ISO format
        }
      }
      return undefined;

    case "multiselect":
      if (Array.isArray(value)) {
        return value.map((v) => String(v));
      }
      if (typeof value === "string") {
        return [value];
      }
      return undefined;

    case "select":
    case "client-select":
    case "user-select":
    case "string":
    default:
      return String(value);
  }
}

/** Option as it appears on a field config: a bare string or `{ value }`. */
type SelectOptionLike = string | { value: unknown };

/**
 * Validates that a value is in the allowed options for select fields
 */
function isValidSelectValue(value: unknown, options?: SelectOptionLike[]): boolean {
  if (!options || options.length === 0) {
    return true; // No validation if no options provided
  }

  const validValues = options.map((opt) => (typeof opt === "string" ? opt : opt.value));
  return validValues.includes(value);
}

/**
 * Extracts and validates form data from LLM response
 */
export function extractFormDataFromLLM<T extends BaseEntity = BaseEntity>(
  llmResponse: string,
  config: SimpleTableConfig<T>
): ExtractedFormData {
  const errors: string[] = [];
  const warnings: string[] = [];
  const data: Record<string, unknown> = {};
  const fieldsMatched: string[] = [];
  const fieldsMissing: string[] = [];

  // Parse JSON response
  const parsedData = parseJSONResponse(llmResponse);
  if (!parsedData) {
    // Provide more detailed error message
    const responsePreview = llmResponse.substring(0, 100);
    const isJsonLike = llmResponse.includes("{") || llmResponse.includes("[");
    errors.push(
      `Could not parse LLM response as JSON. Response preview: "${responsePreview}${llmResponse.length > 100 ? "..." : ""}"${
        isJsonLike ? " (Response contains JSON-like characters but parsing failed)" : ""
      }`
    );
    return { data: {}, errors, warnings, fieldsMatched, fieldsMissing };
  }

  if (Object.keys(parsedData).length === 0) {
    warnings.push("LLM returned empty data. Could not extract any form fields from your prompt.");
    return { data: {}, errors, warnings, fieldsMatched, fieldsMissing };
  }

  // Get editable fields from config
  const editableFields = Object.entries(config.fields).filter(
    ([, field]) => field.editable !== false
  );

  // Process each field in the response
  Object.entries(parsedData).forEach(([fieldName, value]) => {
    // Check if field exists in config
    const fieldConfig = config.fields[fieldName];
    if (!fieldConfig) {
      warnings.push(`Field "${fieldName}" not found in form schema. Skipping.`);
      return;
    }

    // Skip non-editable fields
    if (fieldConfig.editable === false) {
      warnings.push(`Field "${fieldName}" is not editable. Skipping.`);
      return;
    }

    const fieldType = fieldConfig.type || "string";

    // Validate select field values
    if ((fieldType === "select" || fieldType === "client-select") &&
        fieldConfig.options) {
      if (!isValidSelectValue(value, fieldConfig.options)) {
        warnings.push(
          `Value "${value}" for field "${fieldName}" is not in allowed options. Skipping.`
        );
        return;
      }
    }

    // Convert to appropriate type
    const convertedValue = convertToFieldType(value, fieldType);
    if (convertedValue === undefined) {
      warnings.push(
        `Could not convert value "${value}" for field "${fieldName}" to type ${fieldType}. Skipping.`
      );
      return;
    }

    data[fieldName] = convertedValue;
    fieldsMatched.push(fieldName);
  });

  // Check for missing required fields
  editableFields.forEach(([fieldName, fieldConfig]) => {
    if (fieldConfig.required && !data[fieldName]) {
      fieldsMissing.push(fieldName);
    }
  });

  return {
    data,
    errors,
    warnings,
    fieldsMatched,
    fieldsMissing,
  };
}

/**
 * Formats extraction results for user display
 */
export function formatExtractionResults(results: ExtractedFormData): string {
  const parts: string[] = [];

  if (results.fieldsMatched.length > 0) {
    parts.push(`✓ Filled ${results.fieldsMatched.length} field(s): ${results.fieldsMatched.join(", ")}`);
  }

  if (results.warnings.length > 0) {
    parts.push(`⚠ ${results.warnings.join(" ")}`);
  }

  if (results.fieldsMissing.length > 0) {
    parts.push(`ℹ Missing required fields: ${results.fieldsMissing.join(", ")}`);
  }

  return parts.join("\n");
}
